import { CalendarDays, Target, User2 } from 'lucide-react';
import type { GoalListItem } from '@pms/shared';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/dateFormat';
import { GoalProgressBar } from '@/components/goals/GoalProgressBar';
import {
  GoalPriorityBadge,
  GoalRiskBadge,
  GoalStatusBadge,
  GoalTargetTypeBadge,
} from '@/components/goals/GoalBadges';
import { formatDueLabel } from '@/components/goals/goalConstants';

interface Props {
  goal: GoalListItem;
  onOpen: () => void;
  /** Show who owns the goal — useful on team views, noise on "My Goals". */
  showOwner?: boolean;
}

export function GoalCard({ goal, onOpen, showOwner = false }: Props) {
  const dueLabel = formatDueLabel(goal.due_date, goal.status);

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex flex-col gap-3 rounded-xl bg-card p-4 text-left shadow-card transition-shadow hover:shadow-card-hover focus:outline-none focus:ring-2 focus:ring-ring"
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="line-clamp-2 flex-1 text-sm font-semibold leading-snug text-foreground">
          {goal.title}
        </h3>
        <GoalStatusBadge status={goal.status} />
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <GoalTargetTypeBadge type={goal.target_type} />
        <GoalPriorityBadge priority={goal.priority} />
        <GoalRiskBadge isOverdue={goal.is_overdue} isAtRisk={goal.is_at_risk} />
      </div>

      <GoalProgressBar goal={goal} />

      <div className="mt-auto space-y-1.5 border-t border-border/60 pt-2.5 text-[11px] text-muted-foreground">
        {showOwner && (
          <div className="flex items-center gap-1.5">
            <User2 className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">
              {goal.owner_name ?? '—'}
              {goal.department_name ? ` · ${goal.department_name}` : ''}
            </span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <Target className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{goal.category_name ?? 'Uncategorized'}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5 shrink-0" />
            {formatDate(goal.due_date)}
          </span>
          {dueLabel && (
            <span
              className={cn(
                'shrink-0 font-medium',
                goal.is_overdue
                  ? 'text-rose-600 dark:text-rose-400'
                  : goal.is_at_risk
                    ? 'text-amber-600 dark:text-amber-400'
                    : '',
              )}
            >
              {dueLabel}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
